import {
  Description,
  Label,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems
} from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { classNames } from '../../helpers'
import { insertPlaceholder } from './extensions/placeholder-tokens'

const I18N = 'mail_template.edit_mail_template.placeholder_menu.'

// Menü statt Suche, für sechs Einträge braucht es keine. Ein <input> in
// MenuItems wäre in HeadlessUI 2.1.2 kaputt, eine Suche müsste eine Combobox sein.
//
// modal={false} lässt den Editor im Accessibility-Baum. Escape schließt nur das
// Menü, HeadlessUI setzt dabei defaultPrevented und der Dialog bleibt offen.
const PlaceholderMenu = ({
  editor,
  placeholders,
  getInsertRange,
  tabIndex,
  onFocus
}) => {
  const { t } = useTranslation()

  return (
    <Menu as='div' className='relative'>
      <MenuButton
        data-toolbar-item='placeholders'
        tabIndex={tabIndex}
        onFocus={onFocus}
        className={classNames(
          'flex h-8 items-center gap-x-1 rounded px-2 text-sm font-medium text-gray-900',
          'hover:bg-gray-100 data-[open]:bg-gray-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600'
        )}
      >
        {t(I18N + 'button')}
        <ChevronDownIcon aria-hidden='true' className='h-4 w-4 text-gray-500' />
      </MenuButton>
      <MenuItems
        modal={false}
        transition
        className={classNames(
          'absolute left-0 top-full z-20 mt-1 w-80 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
          'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
        )}
      >
        {placeholders
          .filter(({ hidden }) => !hidden)
          .map(({ macro, label, description, value }) => (
            <MenuItem key={macro}>
              <button
                type='button'
                // Der Editor hat den Fokus ans Menü abgegeben, seine Auswahl
                // kennt aber noch die Stelle zum Einfügen
                onClick={() =>
                  insertPlaceholder(editor, macro, { range: getInsertRange() })
                }
                className='flex w-full flex-col items-start gap-y-0.5 px-4 py-2 text-left data-[focus]:bg-blue-50'
              >
                <span className='flex w-full items-baseline justify-between gap-x-3'>
                  <Label
                    as='span'
                    className='text-sm font-medium text-gray-900'
                  >
                    {t(label)}
                  </Label>
                  <span className='font-mono text-xs text-gray-500'>
                    {macro}
                  </span>
                </span>
                <Description as='span' className='text-xs text-gray-500'>
                  {t(description)}
                  {value && (
                    <span className='block text-gray-700'>
                      {t(I18N + 'example', { value })}
                    </span>
                  )}
                </Description>
              </button>
            </MenuItem>
          ))}
      </MenuItems>
    </Menu>
  )
}

PlaceholderMenu.propTypes = {
  editor: PropTypes.object.isRequired,
  placeholders: PropTypes.array.isRequired,
  getInsertRange: PropTypes.func.isRequired,
  tabIndex: PropTypes.number.isRequired,
  onFocus: PropTypes.func.isRequired
}

export default PlaceholderMenu
